// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { StatusCode } from "grpc-web"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { grpc } from "../../../app/rpc/executor"
import { showSnackBar } from "../../snackbar/actions"
import { timesheetFieldClient } from "../../../app/rpc/client"
import * as TimelineActions from "../../timeline/actions"
import * as TimelineSlice from "../../timeline/slices/timeline"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { isGrpcError } from "../../../lib/rpcutils"
import {
  UpdateTimesheetFieldRequest,
  TimesheetField,
  ListTimesheetFieldsRequest,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type UpdateTimesheetFieldRpc = (request: UpdateTimesheetFieldRequest) => Observable<TimesheetField>

export const updateTimesheetField = (updateTimesheetFieldRpc: UpdateTimesheetFieldRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.updateTimesheetField.match),
      map(action => action.payload),
      mergeMap(payload =>
        updateTimesheetFieldRpc(payload.request).pipe(
          mergeMap(response => {
            if (payload.onSuccess) {
              payload.onSuccess()
            }
            const actions: Action[] = [
              TimelineSlice.setTimesheetField(response.toObject()),
            ]
            if (payload.request.getUpdate()?.hasDefaultOptionId()) {
              actions.push(
                Slice.setDefaultTimesheetFieldOptionId({
                  defaultOptionId: payload.request.getUpdate()?.getDefaultOptionId() ?? "",
                  timesheetFieldId: payload.request.getId(),
                })
              )
            }
            return concat(actions)
          }),
          catchError(e => handleError(e, payload)),
        )
      ),
    )

const handleError = (e: unknown, payload: Actions.Payload<UpdateTimesheetFieldRequest>): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return concat([
        TimelineActions.listTimesheetFields({
          request: new ListTimesheetFieldsRequest().setSiteId(payload.request.getSiteId()),
          includeOptions: true,
        }),
        showSnackBar({
          message: t({
            id: "msg_timesheet_field_not_found",
            message: "Timesheet field not found.",
          }),
          messageType: "ERROR",
        }),
      ])
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({
          id: "msg_update_timesheet_field_permission_denied",
          message: "Failed to update timesheet field. You do not have permission.",
        }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_update_timesheet_field_failed",
      message: "Failed to update timesheet field. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
    }),
    messageType: "ERROR",
  }))
}

const updateTimesheetFieldRpc = (request: UpdateTimesheetFieldRequest): Observable<TimesheetField> =>
  grpc(() => timesheetFieldClient.updateTimesheetField(request, null))

export default updateTimesheetField(updateTimesheetFieldRpc)
